// A voice `name` is the provider's own voice id, and those ids are not unique
// across providers: Polly and Azure each ship a "Mia" and an "Aria" under
// different accents. A name on its own therefore identifies neither the voice
// nor the provider to send it to — only the accent it is defined under does.

const collectVoiceMatches = (languages, voiceName) => {
  const matches = [];
  Object.keys(languages).forEach((langKey) => {
    const lang = languages[langKey];
    if (!lang || !lang.Voices) return;
    Object.keys(lang.Voices).forEach((regionKey) => {
      const region = lang.Voices[regionKey];
      const option = (region.voiceOptions || []).find(
        (v) => v.name === voiceName,
      );
      if (option) {
        matches.push({
          ...option,
          provider: option.provider || "polly",
          languageCode: region.languageCode || regionKey,
          language: langKey,
        });
      }
    });
  });
  return matches;
};

/**
 * Resolve a voice name to the config entry that defines it, so callers get the
 * provider, engine and accent belonging to the voice the learner picked.
 *
 * The accent (`languageCode`) is the strongest hint, because two providers can
 * share a name inside a single language — "Aria" is Azure on en-US and Polly on
 * en-NZ, so `learnLang` alone leaves those two indistinguishable.
 *
 * Records stored without an accent leave only a bare name. An ambiguous one
 * then resolves to the language's standard accent (its `Tag`) — the same accent
 * the app already assumes for speech recognition and for Polly's locale — so one
 * rule covers every language and no name needs its own special case.
 *
 * @param languages the config `languages` map
 * @param voiceName the stored voice name, e.g. "Mia"
 * @returns the matched option flattened with `provider`, `languageCode`,
 * `language` and `ambiguous` (true when the accent had to be inferred), or null
 * when the name is not in the config at all
 */
export const resolveVoice = (
  languages,
  voiceName,
  { learnLang, languageCode } = {},
) => {
  if (!languages || !voiceName) return null;

  // Reached only with no stored accent. One name under two accents of the same
  // language is settled by that language's standard accent; `ambiguous` marks the
  // result either way, since nothing here is better than an informed default.
  const guess = (matches) => {
    if (matches.length === 0) return null;
    if (matches.length === 1) return matches[0];
    const standard = matches.find(
      (match) => match.languageCode === languages[match.language]?.Tag,
    );
    return { ...(standard || matches[0]), ambiguous: true };
  };

  const langKey = learnLang ? String(learnLang).toLowerCase() : null;
  const all = collectVoiceMatches(languages, voiceName);

  // A stored accent identifies the voice outright, whatever language it sits
  // under — a phrase can use a voice borrowed from another language, and then
  // the accent is right and `learnLang` is not.
  const forAccent = all.find((match) => match.languageCode === languageCode);
  if (forAccent) return forAccent;

  // With no accent to go on, the learn language narrows the field when it holds
  // the name at all; otherwise every language is fair game.
  const inLanguage = all.filter((match) => match.language === langKey);
  return guess(inLanguage.length > 0 ? inLanguage : all);
};
